import React, { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash.debounce";

import { CloseIcon, SearchNormalIcon } from "components/icons";
import Input, { InputProps } from "components/Input";

interface SearchInputProps extends Omit<InputProps, "value" | "onChange"> {
  controlledValue?: string;
  placeholder?: string;
  withDebounce?: boolean;
  debounceTime?: number;
  keepClearIcon?: boolean;
  isControlled?: boolean;
  fieldClassName?: string;
  onClickLeftIcon?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onSearch: (text: string) => void;
  onTyping?: (isTyping: boolean) => void;
  onClearSearch: () => void;
}
const SearchInput = ({
  defaultValue,
  withDebounce = true,
  debounceTime = 1000,
  keepClearIcon = false,
  fieldClassName,
  onClickLeftIcon,
  onSearch,
  onTyping,
  onClearSearch,
  ...props
}: SearchInputProps) => {
  const [search, setSearch] = useState(defaultValue || "");
  const renderedDefaultValue = useRef(false);
  const isControlled = useRef(false);

  useEffect(() => {
    if (defaultValue) {
      isControlled.current = true;
    }
    if (defaultValue && !renderedDefaultValue.current) {
      setSearch(defaultValue);
      renderedDefaultValue.current = true;
    }
    if (isControlled.current) {
      if (!defaultValue) setSearch("");
    }
  }, [defaultValue]);

  const debouncedSearch = debounce((text: string) => {
    if (text) {
      return onSearch(text);
    }

    onClearSearch();
  }, debounceTime);

  const _onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value;
      setSearch(text);

      if (withDebounce) debouncedSearch(text);
      else onSearch(text);
      if (text) {
        onTyping?.(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSearch, withDebounce, onTyping]
  );

  const resetSearch = useCallback(() => {
    setSearch("");
    onClearSearch();
  }, [onClearSearch]);

  return (
    <Input
      leftIcon={
        <SearchNormalIcon className="text-gray-900" width={16} height={16} />
      }
      rightIcon={
        ((!!search && !props.loading) || keepClearIcon) && (
          <CloseIcon width={14} height={14} />
        )
      }
      inputClassName="!pl-7 placeholder:text-gray-900"
      rightIconClassName="pr-1"
      fieldClassName={fieldClassName}
      onChange={_onChange}
      onClickRightIcon={resetSearch}
      value={search}
      onClickLeftIcon={onClickLeftIcon}
      {...props}
    />
  );
};

export default SearchInput;
